import {
    Checkbox,
    createStyles,
    Divider,
    FormControl,
    Grid,
    Input,
    InputLabel,
    MenuItem,
    Select,
    WithStyles,
    withStyles,
} from '@material-ui/core';
import { inject } from 'mobx-react';
import React from 'react';
import FreeTypeAheadField from 'src/common/FreeTypeAheadField/FreeTypeAheadField';
import IDataTableManager from 'src/common/IDataTableManager';
import { IActionItem, IAssessment, IBranch } from 'src/Definitions';
import { DashboardObservationStatsDto } from 'src/generated_client';
import { IStores } from 'src/Stores';
import ActionItemTableRow from '../dashboardRows/ActionItemRow/ActionItemTableRow';
import ObservationStats from '../dashboardRows/ObservationStats/ObservationStats';

const styles = createStyles({
    formControl: {
        display: 'flex',
        wrap: 'nowrap',
    },
    searchInput: {
        marginBottom: '15px',
        marginLeft: '20px',
    },
    title: { paddingLeft: '20px' },
});

interface IExecutiveDashboardState {
    loaded: boolean;
}

interface IExecutiveDashboardProps {
    allRegionNames?: string[];
    branches?: IBranch[];
    completedDataTableManager?: IDataTableManager<IActionItem>;
    dashboardStats?: DashboardObservationStatsDto;
    dueIn30DataTableManager?: IDataTableManager<IActionItem>;
    getAllBranches?: (
        pageNumber?: number,
        pageSize?: number,
        filter?: string,
        regionsFilter?: string
    ) => void;
    getAssessmentDashboardObservationStats?: (
        branches: string,
        regions: string,
        assessmentId: string | undefined
    ) => Promise<void>;
    selectedAssessment?: IAssessment;
    selectedBranches?: IBranch[];
    selectedRegions?: string[];
    setSelectedAssessment?: (assessment: IAssessment | undefined) => void;
    setSelectedAssessments?: (assessments: IAssessment[]) => void;
    setSelectedRegions?: (regions: string[], loadDataTable: boolean) => void;
    setSelectedBranches?: (branches: IBranch[], loadDataTable: boolean) => void;
    getAllActionItems?: (shouldLoadRegions: boolean, shouldLoadAssignees: boolean) => Promise<void>;
    getColumnSettingsAndActionItems?: () => Promise<void>;
}

class ExecutiveDashboard extends React.Component<
    IExecutiveDashboardProps & WithStyles<typeof styles>,
    IExecutiveDashboardState
> {
    public state: IExecutiveDashboardState = {
        loaded: false,
    };

    public async componentDidMount() {
        await this.pullInitialData();
    }

    private async pullInitialData() {
        this.props.setSelectedAssessment!(undefined);
        this.props.setSelectedBranches!([], false);
        this.props.setSelectedRegions!([], false);
        await Promise.all([
            this.props.getAllActionItems!(true, true),
            this.props.getAssessmentDashboardObservationStats!('', '', undefined),
            this.props.getColumnSettingsAndActionItems!(),
        ]).then(() => {
            this.setState({ loaded: true });
            this.props.setSelectedAssessments!([]);
        });
    }

    public filterRenderValue = (items: string[]) => (selectedItems: string[]) => {
        return items
            .filter((selected) => selectedItems.includes(selected))
            .map((name, index) => `${index ? ', ' : ''} ${name}`);
    };

    public render() {
        const {
            allRegionNames,
            branches,
            dashboardStats,
            dueIn30DataTableManager,
            completedDataTableManager,
            getAllBranches,
            getAssessmentDashboardObservationStats,
            selectedBranches,
            selectedRegions,
            setSelectedBranches,
            setSelectedRegions,
        } = this.props;

        function handleRegionSelectChange(event: React.ChangeEvent<{ value: any }>) {
            const newSelectedRegions = event.target.value as string[];
            setSelectedRegions!(newSelectedRegions, true);
            getAssessmentDashboardObservationStats!(
                selectedBranches ? selectedBranches.map((x) => (x ? x.name : '')).toString() : '',
                newSelectedRegions ? newSelectedRegions.toString() : '',
                undefined
            );
        }

        function handleUpdateBranchSearch(newFilter: string) {
            getAllBranches!(
                undefined,
                5000,
                newFilter,
                selectedRegions ? selectedRegions.toString() : undefined
            );
        }

        function getBranchValue(branch: IBranch) {
            return branch ? branch.id : '';
        }

        function getBranchName(branch: IBranch) {
            return branch ? branch.name : '';
        }

        function handleChipChange(chipBranches: IBranch[]) {
            setSelectedBranches!(chipBranches, true);
            getAssessmentDashboardObservationStats!(
                chipBranches ? chipBranches.map((x) => (x ? x.name : '')).toString() : '',
                selectedRegions ? selectedRegions.toString() : '',
                undefined
            );
        }

        const filterByRegion = (
            <FormControl className={this.props.classes.formControl}>
                <InputLabel>Regions</InputLabel>
                <Select
                    data-cy="regionsFilter"
                    multiple
                    value={selectedRegions || []}
                    onChange={handleRegionSelectChange}
                    input={<Input id="select-multiple" />}
                    renderValue={this.filterRenderValue(allRegionNames || [])}
                >
                    {(allRegionNames || []).map((name: string, index: number) => (
                        <MenuItem key={index} value={name}>
                            <Checkbox checked={(selectedRegions as any).includes(name)} />
                            {name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );

        return (
            <Grid container direction="column">
                <h1 className={this.props.classes.title}>Executive Dashboard Rollup</h1>
                <Grid item>
                    <Grid container direction="row">
                        <Grid item xs={6}>
                            <FormControl className={this.props.classes.searchInput}>
                                <FreeTypeAheadField
                                    label="Branch Search &#9662;"
                                    fullWidth={true}
                                    getName={getBranchName}
                                    getValue={getBranchValue}
                                    getItems={handleUpdateBranchSearch}
                                    items={branches || []}
                                    onChipChange={handleChipChange}
                                    selectedItems={selectedBranches || []}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            {filterByRegion}
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item>
                    {dashboardStats ? <ObservationStats dashboardStats={dashboardStats} /> : <></>}
                </Grid>
                <Grid item>
                    <Divider />
                </Grid>
                <Grid item>
                    {this.state.loaded ? (
                        <div id="actionItemsRow">
                            <ActionItemTableRow
                                dueIn30DataTableManager={dueIn30DataTableManager}
                                completedDataTableManager={completedDataTableManager}
                            />
                        </div>
                    ) : (
                        <></>
                    )}
                </Grid>
            </Grid>
        );
    }
}

const InjectedExecutiveDashboard = inject<
    IStores,
    IExecutiveDashboardProps,
    Partial<IExecutiveDashboardProps>,
    any
>((stores: IStores) => ({
    allRegionNames: stores.actionItems.allRegionNames,
    branches: stores.branches.branches,
    completedDataTableManager: stores.actionItems.completedDataTableStore,
    dashboardStats: stores.assessments.dashboardStats,
    dueIn30DataTableManager: stores.actionItems.dueIn30DataTableStore,
    getAllActionItems: stores.actionItems.getAllActionItems,
    getAllBranches: stores.branches.getAllBranches,
    getAssessmentDashboardObservationStats: stores.assessments.getAssessmentDashboardStats,
    getColumnSettingsAndActionItems: stores.actionItems.getColumnSettingsAndActionItems,
    selectedAssessment: stores.assessments.selectedAssessment,
    selectedBranches: stores.actionItems.selectedBranches,
    selectedRegions: stores.actionItems.selectedRegions,
    setSelectedAssessment: stores.assessments.setSelectedAssessment,
    setSelectedAssessments: stores.actionItems.setSelectedAssessments,
    setSelectedBranches: stores.actionItems.setSelectedBranches,
    setSelectedRegions: stores.actionItems.setSelectedRegions,
}))(withStyles(styles)(ExecutiveDashboard));

export default InjectedExecutiveDashboard;
export { IExecutiveDashboardProps, IExecutiveDashboardState };



// WEBPACK FOOTER //
// ./src/executiveDashboard/ExecutiveDashboard.tsx